import { api } from "../index";

export interface DashboardSummary {
  totalUsers: number;
  totalImages: number;
  pendingCollaborations: number;
  pendingImages: number;
  newUsersThisPeriod: number;
  newImagesThisPeriod: number;
  usersByType: {
    common: number;
    collaborator: number;
    admin: number;
  };
  period: {
    from: string;
    to: string;
  };
}

export interface DashboardActivity {
  id: string;
  type:
    | "user_registration"
    | "image_upload"
    | "collaboration_submitted"
    | "image_approved"
    | "image_rejected";
  description: string;
  date: string;
  user: {
    id: string;
    name: string;
  };
  entity: {
    type: "image" | "collaboration" | "user";
    id: string;
    status?: string;
  };
}

export interface DashboardActivitiesResponse {
  items: DashboardActivity[];
  total: number;
  limit: number;
  offset: number;
  period: {
    from: string;
    to: string;
  };
}

export interface TimeSeriesPoint {
  date: string;
  value: number;
}

export interface DashboardTimeSeriesResponse {
  metric: "uploads" | "registrations";
  groupBy: "day" | "week" | "month";
  points: TimeSeriesPoint[];
  period: {
    from: string;
    to: string;
  };
}

export interface DashboardSummaryParams {
  period?: "7d" | "30d" | "90d";
  from?: string;
  to?: string;
}

export interface DashboardActivitiesParams {
  limit?: number;
  offset?: number;
  period?: "7d" | "30d" | "90d";
  from?: string;
  to?: string;
  types?: string;
}

export interface DashboardTimeSeriesParams {
  metric: "uploads" | "registrations";
  groupBy?: "day" | "week" | "month";
  period?: "7d" | "30d" | "90d";
  from?: string;
  to?: string;
}

/**
 * Obtém o resumo do dashboard com KPIs principais
 */
export const getDashboardSummary = async (
  params: DashboardSummaryParams = {},
  token: string
): Promise<DashboardSummary> => {
  const queryParams = new URLSearchParams();

  if (params.period) queryParams.append("period", params.period);
  if (params.from) queryParams.append("from", params.from);
  if (params.to) queryParams.append("to", params.to);

  const response = await api.get(
    `/admin/dashboard/summary?${queryParams.toString()}`,
    {
      headers: {
        "X-CSRF-Token": token,
      },
    }
  );

  return response.data;
};

/**
 * Obtém a lista de atividades recentes (paginada)
 */
export const getDashboardActivities = async (
  params: DashboardActivitiesParams = {},
  token: string
): Promise<DashboardActivitiesResponse> => {
  const queryParams = new URLSearchParams();

  if (params.limit) queryParams.append("limit", params.limit.toString());
  if (params.offset) queryParams.append("offset", params.offset.toString());
  if (params.period) queryParams.append("period", params.period);
  if (params.from) queryParams.append("from", params.from);
  if (params.to) queryParams.append("to", params.to);
  if (params.types) queryParams.append("types", params.types);

  const response = await api.get(
    `/admin/dashboard/activities?${queryParams.toString()}`,
    {
      headers: {
        "X-CSRF-Token": token,
      },
    }
  );

  return response.data;
};

/**
 * Obtém série temporal para gráficos
 */
export const getDashboardTimeSeries = async (
  params: DashboardTimeSeriesParams,
  token: string
): Promise<DashboardTimeSeriesResponse> => {
  const queryParams = new URLSearchParams();

  queryParams.append("metric", params.metric);
  if (params.groupBy) queryParams.append("groupBy", params.groupBy);
  if (params.period) queryParams.append("period", params.period);
  if (params.from) queryParams.append("from", params.from);
  if (params.to) queryParams.append("to", params.to);

  const response = await api.get(
    `/admin/dashboard/timeseries?${queryParams.toString()}`,
    {
      headers: {
        "X-CSRF-Token": token,
      },
    }
  );

  return response.data;
};

// ========== NOVAS ROTAS DE DISTRIBUIÇÃO ==========

export interface DistributionItem {
  species?: string;
  region?: string;
  city?: string;
  count: number;
}

export interface DistributionResponse {
  top: DistributionItem[];
  others: number;
  total: number;
  period: {
    from: string;
    to: string;
  };
}

export interface DistributionParams {
  period?: "7d" | "30d" | "90d";
  from?: string;
  to?: string;
  limit?: number;
}

/**
 * Obtém distribuição por espécie (Top N + Outros)
 */
export const getSpeciesDistribution = async (
  params: DistributionParams = {},
  token: string
): Promise<DistributionResponse> => {
  const queryParams = new URLSearchParams();

  if (params.period) queryParams.append("period", params.period);
  if (params.from) queryParams.append("from", params.from);
  if (params.to) queryParams.append("to", params.to);
  if (params.limit) queryParams.append("limit", params.limit.toString());

  const response = await api.get(
    `/admin/dashboard/distribution/species?${queryParams.toString()}`,
    {
      headers: {
        "X-CSRF-Token": token,
      },
    }
  );

  return response.data;
};

/**
 * Obtém distribuição por região (Top N + Outros)
 */
export const getRegionsDistribution = async (
  params: DistributionParams = {},
  token: string
): Promise<DistributionResponse> => {
  const queryParams = new URLSearchParams();

  if (params.period) queryParams.append("period", params.period);
  if (params.from) queryParams.append("from", params.from);
  if (params.to) queryParams.append("to", params.to);
  if (params.limit) queryParams.append("limit", params.limit.toString());

  const response = await api.get(
    `/admin/dashboard/distribution/regions?${queryParams.toString()}`,
    {
      headers: {
        "X-CSRF-Token": token,
      },
    }
  );

  return response.data;
};

/**
 * Obtém distribuição por município (Top N + Outros)
 */
export const getCitiesDistribution = async (
  params: DistributionParams = {},
  token: string
): Promise<DistributionResponse> => {
  const queryParams = new URLSearchParams();

  if (params.period) queryParams.append("period", params.period);
  if (params.from) queryParams.append("from", params.from);
  if (params.to) queryParams.append("to", params.to);
  if (params.limit) queryParams.append("limit", params.limit.toString());

  const response = await api.get(
    `/admin/dashboard/distribution/cities?${queryParams.toString()}`,
    {
      headers: {
        "X-CSRF-Token": token,
      },
    }
  );

  return response.data;
};
